import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { Subject, take, Observable, takeUntil, debounceTime, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PrehoxPaginatorComponent } from 'app/shared/components/prehox-paginator/prehox-paginator.component';
import { NgxMaskPipe } from 'ngx-mask';
import { IFiltroPaginacao } from 'app/shared/interfaces/filtro';
import { PesquisaDialogData } from 'app/shared/interfaces/pesquisa-dialog-data.interface';
import { Debounce } from 'app/shared/decorators/debouce-decorator';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
@Component({
    selector: 'pesquisa-dialog',
    templateUrl: './pesquisa-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatIconModule,
        MatInputModule,
        MatTableModule,
        MatProgressSpinnerModule,
        PrehoxPaginatorComponent,
        NgxMaskPipe,
        CurrencyPipe,
        AutoTranslatePipe,
        AutoTranslateDirective,
    ],
})
export class PesquisaDialogComponent implements OnInit, OnDestroy {
    @ViewChild(PrehoxPaginatorComponent) paginator: PrehoxPaginatorComponent;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    dataSource: MatTableDataSource<any> = new MatTableDataSource();
    dataSourceOriginal: MatTableDataSource<any> = new MatTableDataSource();
    isLoading;
    hasMore = false;
    totalItems = 0;
    filtro: IFiltroPaginacao<any> = {
        page: 1,
        text: '',
        limit: 10,
    };
    columns: string[] = [];
    pageSize = 10;
    pageSizeOptions = [5, 10, 20, 50];
    searchInputControl: UntypedFormControl = new UntypedFormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PesquisaDialogData<any>,
        public matDialogRef: MatDialogRef<PesquisaDialogComponent>,
        public _cdr: ChangeDetectorRef,
    ) {
        matDialogRef.addPanelClass('card-dialog');
    }

    ngOnInit(): void {
        this.searchInputControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(350),
                switchMap((query) => {
                    this.filtrar(query);
                    return query;
                }),
            )
            .subscribe();

        // Initialize filter from passed data
        this.filtro = { ...this.data.filtro };
        this.columns = this.data.displayColumns.map((e) => e.column);

        // Initial data loading
        this.loadData();
    }

    loadData(): void {
        this.isLoading = true;
        this.data.service
            .list(this.filtro)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.dataSource.data = response.items;
                this.dataSourceOriginal.data = [...this.dataSource.data];
                this.totalItems = response.meta.totalItems;
                this.hasMore = this.totalItems > this.dataSource.data.length;
                this.isLoading = false;
                this._cdr.detectChanges();
            });
    }

    filtrar(query: string): void {
        if (!query || query.trim() === '') {
            // Reset to original data when search is cleared
            this.filtro.text = '';
            this.filtro.page = 1;
            if (this.paginator) {
                this.paginator.firstPage();
            }
            this.loadData();
            return;
        }

        if (query.length >= 3) {
            this.filtro.text = query;
            this.filtro.page = 1;
            if (this.paginator) {
                this.paginator.firstPage();
            }
            this.loadData();
        }
    }

    clear(): void {
        this.matDialogRef.close();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    selecionar(e) {
        this.matDialogRef.close(e);
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    getColumnType(column: any): string {
        if (column?.mask) return 'mask';
        if (column?.currency) return 'currency';
        if (column?.decimal) return 'decimal';
        if (column?.date) return 'date';
        if (column?.datetime) return 'datetime';
        if (column?.boolean) return 'boolean';
        return 'default';
    }

    getNestedValue(obj: any, path: string): any {
        if (!obj || !path) return '';

        // Se o path contém pontos, navega pelas propriedades aninhadas
        if (path.includes('.')) {
            return path.split('.').reduce((current, key) => {
                return current && current[key] !== undefined ? current[key] : '';
            }, obj);
        }

        // Se não contém pontos, retorna a propriedade direta
        return obj[path] || '';
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    pageEvent(event: any): void {
        this.filtro.page = event.pageIndex + 1;
        this.filtro.limit = event.pageSize;
        this.loadData();
    }
}
